import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { AED, downloadReceipt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Download } from "lucide-react";

export default function PaymentSuccess({ kind }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [msg, setMsg] = useState("Confirming your payment…");
  const [stuck, setStuck] = useState(false);
  const [order, setOrder] = useState(null); // confirmed purchase transaction
  const [topup, setTopup] = useState(null); // confirmed deposit top-up {paid, balance}
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sid = params.get("session_id");
    if (!sid) { navigate("/"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refreshUser().catch(() => {});
          if (kind === "deposit") {
            if (data.activated_listing_id) {
              toast.success("Deposit paid — your listing is live.");
              navigate(`/listing/${data.activated_listing_id}`);
            } else if (data.pending_draft_id) {
              const missing = (data.pending_missing || []).join(", ");
              toast.message(missing ? `Deposit paid. Your listing still needs ${missing} before it can go live.` : "Deposit paid. Your listing needs a few more details before it can go live.");
              navigate(`/sell?draft=${data.pending_draft_id}`);
            } else {
              toast.success("Deposit paid — you're ready to list!");
              navigate("/profile");
            }
          } else if (kind === "topup") {
            let bal = null;
            try { const me = await api.get("/auth/me"); bal = me.data?.deposit_balance; } catch (_) { /* noop */ }
            const paid = Math.round((data.amount || 0)) / 100;
            setTopup({ paid, balance: bal });
            toast.success(`Deposit topped up — ${AED(paid)} paid`);
            return;
          } else {
            toast.success("Payment confirmed — your order is placed!");
            let txn = null;
            if (data.transaction_id) {
              try { const r = await api.get(`/transactions/${data.transaction_id}`); txn = r.data; } catch (_) { /* still show button */ }
            }
            setOrder(txn || { id: data.transaction_id });
          }
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          toast.error("Payment was not completed"); navigate("/"); return;
        }
      } catch (_) { /* keep polling */ }
      if (++tries > 20) { setMsg("Still processing — your payment may take a moment to confirm."); setStuck(true); return; }
      setTimeout(poll, 1500);
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const downloadReceiptClick = async () => {
    try { await downloadReceipt(order.id, `CR-${order.id.slice(0, 8).toUpperCase()}`); }
    catch (e) { toast.error(e.response?.status === 403 ? "You can only download your own receipts" : "Could not download receipt"); }
  };

  if (topup) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4 py-10" data-testid="payment-success">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 p-8 text-center shadow-sm" data-testid="topup-confirmation">
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-500" />
          <h1 className="font-heading text-2xl font-bold text-slate-900">Top-up confirmed</h1>
          <p className="mt-1 text-sm text-slate-500">Your security deposit has been topped up.</p>
          <div className="mt-6 space-y-2 rounded-xl bg-slate-50 p-4 text-left text-sm">
            <div className="flex justify-between gap-4"><span className="text-slate-500">Amount paid</span><span className="font-medium text-slate-900" data-testid="topup-amount-paid">{AED(topup.paid)}</span></div>
            {topup.balance != null && (
              <div className="flex justify-between gap-4"><span className="text-slate-500">New balance</span><span className="font-medium text-slate-900" data-testid="topup-confirmed-balance">{AED(topup.balance)}</span></div>
            )}
          </div>
          <button data-testid="topup-go-profile" onClick={() => navigate("/profile")}
            className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary/90">
            Go to my profile
          </button>
        </div>
      </div>
    );
  }

  if (order) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4 py-10" data-testid="payment-success">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 p-8 text-center shadow-sm" data-testid="order-confirmation">
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-500" />
          <h1 className="font-heading text-2xl font-bold text-slate-900">Payment confirmed</h1>
          <p className="mt-1 text-sm text-slate-500">Your order is placed and your payment is held securely.</p>

          {(order.listing_title || order.total != null) && (
            <div className="mt-6 space-y-2 rounded-xl bg-slate-50 p-4 text-left text-sm" data-testid="order-confirmation-details">
              {order.id && (
                <div className="flex justify-between gap-4"><span className="text-slate-500">Order reference</span><span className="font-mono font-medium text-slate-900">CR-{order.id.slice(0, 8).toUpperCase()}</span></div>
              )}
              {order.listing_title && (
                <div className="flex justify-between gap-4"><span className="text-slate-500">Item</span><span className="text-right font-medium text-slate-900">{order.listing_title}</span></div>
              )}
              {order.total != null && (
                <div className="flex justify-between gap-4"><span className="text-slate-500">Amount paid</span><span className="font-medium text-slate-900">{AED(order.total)}</span></div>
              )}
            </div>
          )}

          <button data-testid="success-download-receipt" onClick={downloadReceiptClick}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
            <Download className="h-4 w-4" /> Download receipt
          </button>
          <button data-testid="success-go-orders" onClick={() => navigate("/orders?tab=purchases")}
            className="mt-3 inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary/90">
            Go to my orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-slate-600" data-testid="payment-success">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm">{msg}</p>
      {stuck && (
        <div className="mt-2 flex gap-3">
          <button data-testid="payment-go-orders" onClick={() => navigate("/orders?tab=purchases")} className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white">Go to Orders</button>
          <button data-testid="payment-go-profile" onClick={() => navigate("/profile")} className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700">Go to Profile</button>
        </div>
      )}
    </div>
  );
}
